/**
 * TableEditor — inner tab for editing a table's schema (columns + options).
 *
 * Tabs: Columns, Options, Indexes, Foreign keys, Triggers. A column's
 * charset / collation / ON UPDATE are loaded and round-tripped (collation is
 * shown as the type's tooltip; ON UPDATE has its own toggle) so a save never
 * silently converts or strips them.
 *
 * Each editor row tracks `originalName` so the backend's diff engine can
 * emit CHANGE rather than DROP+ADD for renames. New rows have empty
 * originalName.
 *
 * Save flow: → Preview (POST .../schema/preview returns the ALTER plan) →
 * confirm in a modal that lists the steps as plain English + SQL → Apply.
 *
 * Props:
 *   connectionId, connectionName
 *   database, table
 *   active            bool — true when this editor is currently in the foreground
 *   registerDirty?    fn   — optional. Receives an accessor that returns whether
 *                            the editor has unsaved edits. The modal wrapper uses
 *                            it to guard close.
 *   onTableRenamed?   (newName) — fired after a successful save if the table
 *                                 has been renamed (TODO for a future slice)
 */
import { createSignal, createResource, createEffect, onMount, onCleanup, For, Show } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import {
  loadTableSchema, previewTableSchema, applyTableSchema,
  previewCreateTable, createTable, loadDataTypes,
} from '../lib/tableschema.js';
import { toast } from '../lib/toast.js';
import CodeMirrorSQL from './CodeMirrorSQL.jsx';
import { sqlSchemaFor } from '../lib/schema.js';

const ENGINES    = ['InnoDB', 'MyISAM', 'MEMORY', 'ARCHIVE'];
const ROWFORMATS = ['', 'DEFAULT', 'DYNAMIC', 'COMPACT', 'COMPRESSED', 'REDUNDANT', 'FIXED'];

export default function TableEditor(props) {
  const [activeTab, setActiveTab] = createSignal('columns'); // 'columns' | 'options' | 'indexes' | 'fkeys' | 'triggers'

  // Create mode: build a brand-new table. No server load; the table name is
  // user-entered; preview/apply hit the create-table endpoints.
  const createMode = () => !!props.createMode;
  const [tableName, setTableName] = createSignal(props.table || '');
  // Autocomplete for the trigger bodies (NEW./OLD. → this table's columns).
  const sqlSchema = sqlSchemaFor(() => props.connectionId, () => props.database);

  // Server state — re-fetched on mount; cached locally so unsaved edits
  // survive inner-tab switches. Skipped entirely in create mode (no table yet).
  const [server, { refetch }] = createResource(
    () => (createMode() ? false : [props.connectionId, props.database, props.table]),
    ([id, db, t]) => loadTableSchema(id, db, t),
  );

  // Caps-driven set of column data types this server supports, for the type
  // dropdown. Keyed on the connection only (server-level, not per-table/db), so it
  // resolves once and is shared by create mode and edit mode. [] until loaded /
  // on error — the TypeEditor then preserves each column's existing type verbatim.
  const [dataTypes] = createResource(
    () => props.connectionId,
    (id) => loadDataTypes(id),
  );
  const typeList = () => dataTypes() ?? [];

  // Editor state — initialised from the server snapshot once it loads.
  const [columns, setColumns] = createStore([]);
  const [options, setOptions] = createStore({
    engine: '', charset: '', collation: '', autoIncrement: 0, rowFormat: '', comment: '',
  });
  // Triggers tab: list of trigger cards. originalName lets the backend
  // distinguish a rename (drop old, create new) from "drop + add of unrelated".
  const [triggers, setTriggers]   = createStore([]);
  const [indexes, setIndexes]     = createStore([]);
  const [fkeys,   setFkeys]       = createStore([]);
  const [dirty,   setDirty]   = createSignal(false);
  const [loaded,  setLoaded]  = createSignal(false);

  // Preview / apply state.
  const [previewing, setPreviewing] = createSignal(false);
  const [previewPlan, setPreviewPlan] = createSignal(null); // [{ sql, summary }]
  // Version-compatibility violations the target server can't support (JSON,
  // fractional-seconds, utf8mb4, index COMMENT — see dbcaps.Check). Populated by
  // the preview call; Apply is blocked on the server unless the user explicitly
  // acknowledges via the modal's checkbox (PreviewModal → confirmApply(override)).
  const [violations, setViolations] = createSignal([]);
  const [applying,    setApplying]    = createSignal(false);

  // Initialise editor state from the loaded server snapshot. Each column gets
  // an `originalName` so renames can be detected server-side.
  const initFromServer = () => {
    const r = server();
    if (!r) return;
    setColumns(
      (r.columns ?? []).map((c) => ({
        name:          c.name,
        originalName:  c.name,
        type:          c.type ?? '',
        nullable:      !!c.nullable,
        default:       c.default ?? null,
        autoIncrement: !!c.autoIncrement,
        isPrimary:     !!c.isPrimary,
        comment:       c.comment ?? '',
        // Generated (computed) columns + expression defaults: carried through
        // untouched so an apply never strips the AS (expr) clause or re-quotes
        // a DEFAULT (expr) into a string (the backend also inherits both as a
        // second net). extra keeps the VIRTUAL/STORED kind readable in the row.
        generationExpr: c.generationExpr ?? '',
        defaultIsExpr:  !!c.defaultIsExpr,
        extra:          c.extra ?? '',
        // Round-tripped so a MODIFY for an unrelated change keeps them: without
        // CHARACTER SET / COLLATE the column is converted to the table default
        // (lossy), without ON UPDATE it stops auto-updating.
        characterSet:   c.characterSet ?? '',
        collation:      c.collation ?? '',
        onUpdate:       onUpdateFromExtra(c.extra),
        pkOrdinal:      c.pkOrdinal ?? 0,
      })),
    );
    const o = r.options ?? {};
    setOptions({
      engine:        o.engine ?? '',
      charset:       o.charset ?? '',
      collation:     o.collation ?? '',
      autoIncrement: o.autoIncrement ?? 0,
      rowFormat:     o.rowFormat ?? '',
      comment:       o.comment ?? '',
    });
    setTriggers(
      (r.triggers ?? []).map((t) => ({
        name:         t.name,
        originalName: t.name,
        timing:       (t.timing  || 'BEFORE').toUpperCase(),
        event:        (t.event   || 'INSERT').toUpperCase(),
        statement:    t.statement || '',
      })),
    );
    setIndexes(
      (r.indexes ?? []).map((ix) => ({
        name:         ix.name,
        originalName: ix.name,
        isUnique:     !!ix.isUnique,
        type:         (ix.type || 'BTREE').toUpperCase(),
        columnsText:  indexColsToText(ix.columns || []),
        comment:      ix.comment || '',
      })),
    );
    setFkeys(
      (r.foreignKeys ?? []).map((fk) => ({
        name:             fk.name,
        originalName:     fk.name,
        columnsText:      (fk.columns || []).join(', '),
        referencedSchema: fk.referencedSchema || '',
        referencedTable:  fk.referencedTable  || '',
        referencedColsText: (fk.referencedCols || []).join(', '),
        onUpdate:         (fk.onUpdate || 'RESTRICT').toUpperCase(),
        onDelete:         (fk.onDelete || 'RESTRICT').toUpperCase(),
      })),
    );
    setDirty(false);
    setLoaded(true);
  };

  // Blank starting point for a new table: one auto-increment PK id column +
  // InnoDB defaults. The user edits from here.
  const initBlank = () => {
    setColumns([{
      name: 'id', originalName: '', type: 'int unsigned', nullable: false,
      default: null, autoIncrement: true, isPrimary: true, comment: '',
      onUpdate: '', defaultIsExpr: false,
    }]);
    setOptions({ engine: 'InnoDB', charset: '', collation: '', autoIncrement: 0, rowFormat: '', comment: '' });
    setTriggers([]);
    setIndexes([]);
    setFkeys([]);
    setDirty(false);
    setLoaded(true);
  };

  // Bootstrap the editor state — a blank spec in create mode, otherwise from the
  // server snapshot whenever a fresh payload arrives (mount, after apply, Reload).
  createEffect(() => {
    if (createMode()) {
      if (!loaded()) initBlank();
      return;
    }
    if (!server.loading && server() && !loaded()) initFromServer();
  });

  // Mutators — each marks the editor dirty.
  // IMPORTANT: use the path-based store setter (setStore(i, patch)) which
  // *merges* patch into the existing item without replacing the item's
  // reference. `setColumns(produce(arr => { arr[i] = {...arr[i], ...patch}}))`
  // would work for the data but breaks `<For>` keyed rendering: the new
  // object reference at index `i` makes Solid throw away the row's JSX and
  // re-create it, which unmounts the input and the user loses focus after
  // each character. The merge form keeps the proxy identity stable.
  const touch = () => { if (!dirty()) setDirty(true); };
  const updateCol = (i, patch) => { setColumns(i, patch); touch(); };
  const addCol = () => {
    setColumns(produce((arr) => {
      arr.push({
        name: '', originalName: '', type: 'varchar(255)', nullable: true,
        default: null, autoIncrement: false, isPrimary: false, comment: '',
        onUpdate: '', defaultIsExpr: false,
      });
    }));
    touch();
  };
  const removeCol = (i) => {
    setColumns(produce((arr) => { arr.splice(i, 1); }));
    touch();
  };
  const moveCol = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= columns.length) return;
    setColumns(produce((arr) => {
      const [c] = arr.splice(i, 1);
      arr.splice(j, 0, c);
    }));
    touch();
  };
  const setOption = (key, value) => {
    setOptions(key, value);
    touch();
  };

  // ── Triggers ──
  const updateTrigger = (i, patch) => { setTriggers(i, patch); touch(); };
  const addTrigger = () => {
    setTriggers(produce((arr) => {
      arr.push({
        name: '', originalName: '',
        timing: 'BEFORE', event: 'INSERT',
        statement: 'BEGIN\n  -- trigger body\nEND',
      });
    }));
    touch();
  };
  const removeTrigger = (i) => {
    setTriggers(produce((arr) => { arr.splice(i, 1); }));
    touch();
  };

  // ── Indexes ──
  const updateIndex = (i, patch) => { setIndexes(i, patch); touch(); };
  const addIndex = () => {
    setIndexes(produce((arr) => {
      arr.push({
        name: '', originalName: '',
        isUnique: false, type: 'BTREE',
        columnsText: '', comment: '',
      });
    }));
    touch();
  };
  const removeIndex = (i) => {
    setIndexes(produce((arr) => { arr.splice(i, 1); }));
    touch();
  };

  // ── Foreign keys ──
  const updateFK = (i, patch) => { setFkeys(i, patch); touch(); };
  const addFK = () => {
    setFkeys(produce((arr) => {
      arr.push({
        name: '', originalName: '',
        columnsText: '', referencedSchema: '',
        referencedTable: '', referencedColsText: '',
        onUpdate: 'RESTRICT', onDelete: 'RESTRICT',
      });
    }));
    touch();
  };
  const removeFK = (i) => {
    setFkeys(produce((arr) => { arr.splice(i, 1); }));
    touch();
  };

  // Default values are nullable: blank string in the input means literal '',
  // a separate "no default" checkbox controls whether to emit the clause at all.
  // The "expr" toggle (defaultIsExpr) is explicit and visible: a typed
  // expression is never silently quoted, and a loaded expression flag can be
  // turned off when the default is replaced with a literal.
  const setDefaultText = (i, value) => updateCol(i, { default: value });
  const setHasDefault  = (i, has)   => updateCol(i, has ? { default: '' } : { default: null, defaultIsExpr: false });

  // ── Save flow ─────────────────────────────────────────────────────────────
  const buildRequest = () => ({
    // This editor sends DESC / functional index parts explicitly (the backend
    // otherwise keeps the current ones for clients that don't model them).
    keyPartsModeled: true,
    columns: columns.map((c) => ({
      name:          c.name.trim(),
      originalName:  c.originalName,
      type:          c.type.trim(),
      nullable:      c.nullable,
      default:       c.default,
      autoIncrement: c.autoIncrement,
      isPrimary:     c.isPrimary,
      comment:       c.comment,
      // Pass-through for generated columns / expression defaults (loaded in
      // initFromServer; blank on hand-added rows): kind derives from EXTRA's
      // vocabulary across forks.
      defaultIsExpr:  c.default !== null && !!c.defaultIsExpr,
      generationExpr: c.generationExpr || '',
      generatedKind:  c.generationExpr
        ? (/virtual/i.test(c.extra || '') ? 'VIRTUAL' : 'STORED')
        : '',
      // Always sent (explicit '' = none) so the backend keeps exactly what the
      // editor shows; the backend omits charset/collation on non-text types and
      // ON UPDATE on non-temporal ones.
      characterSet:   c.characterSet || '',
      collation:      c.collation || '',
      onUpdate:       c.onUpdate || '',
      pkOrdinal:      c.pkOrdinal || 0,
    })),
    options: {
      engine:        options.engine,
      charset:       options.charset,
      collation:     options.collation,
      autoIncrement: Number(options.autoIncrement) || 0,
      rowFormat:     options.rowFormat,
      comment:       options.comment,
    },
    triggers: triggers.map((t) => ({
      name:         t.name.trim(),
      originalName: t.originalName,
      timing:       t.timing,
      event:        t.event,
      statement:    t.statement,
    })),
    indexes: indexes.map((ix) => ({
      name:         ix.name.trim(),
      originalName: ix.originalName,
      isUnique:     ix.isUnique,
      type:         ix.type,
      columns:      parseIndexColumns(ix.columnsText),
      comment:      ix.comment,
    })),
    foreignKeys: fkeys.map((fk) => ({
      name:             fk.name.trim(),
      originalName:     fk.originalName,
      columns:          parseColumnList(fk.columnsText),
      referencedSchema: fk.referencedSchema.trim(),
      referencedTable:  fk.referencedTable.trim(),
      referencedCols:   parseColumnList(fk.referencedColsText),
      onUpdate:         fk.onUpdate,
      onDelete:         fk.onDelete,
    })),
  });

  const startPreview = async () => {
    if (previewing()) return;
    if (createMode() && !tableName().trim()) { toast.error('Enter a table name.'); return; }
    // Cheap client-side checks first — backend will also reject these but the
    // toast is faster than the round-trip.
    for (const c of columns) {
      if (!c.name.trim()) { toast.error('A column has no name.'); return; }
      if (!c.type.trim()) { toast.error(`Column "${c.name}" has no type.`); return; }
    }
    for (const t of triggers) {
      if (!t.name.trim())      { toast.error('A trigger has no name.'); return; }
      if (!t.statement.trim()) { toast.error(`Trigger "${t.name}" has an empty body.`); return; }
    }
    for (const ix of indexes) {
      if (!ix.name.trim())                       { toast.error('An index has no name.'); return; }
      if (parseIndexColumns(ix.columnsText).length === 0) {
        toast.error(`Index "${ix.name}" has no columns.`); return;
      }
    }
    for (const fk of fkeys) {
      if (!fk.name.trim())                            { toast.error('A foreign key has no name.'); return; }
      if (!fk.referencedTable.trim())                 { toast.error(`FK "${fk.name}" needs a referenced table.`); return; }
      const cs = parseColumnList(fk.columnsText);
      const rs = parseColumnList(fk.referencedColsText);
      if (cs.length === 0 || rs.length === 0) {
        toast.error(`FK "${fk.name}" needs local and referenced columns.`); return;
      }
      if (cs.length !== rs.length) {
        toast.error(`FK "${fk.name}": local (${cs.length}) and referenced (${rs.length}) column counts must match.`);
        return;
      }
    }
    setPreviewing(true);
    try {
      const r = createMode()
        ? await previewCreateTable(props.connectionId, props.database, tableName().trim(), buildRequest())
        : await previewTableSchema(props.connectionId, props.database, props.table, buildRequest());
      setPreviewPlan(r.steps ?? []);
      setViolations(r.violations ?? []);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  const closePreview = () => { setPreviewPlan(null); setViolations([]); };

  // override: true once the user has explicitly acknowledged the modal's
  // violation warning (PreviewModal's checkbox) — forwarded to the server so it
  // proceeds despite a construct the target's version can't support.
  const confirmApply = async (override) => {
    if (applying()) return;
    setApplying(true);
    try {
      if (createMode()) {
        const name = tableName().trim();
        const r = await createTable(props.connectionId, props.database, name, buildRequest(), override);
        toast.success(`Table "${name}" created (${r.applied} statement${r.applied === 1 ? '' : 's'})`);
        setPreviewPlan(null);
        setViolations([]);
        setDirty(false);
        props.onCreated?.(name); // parent refreshes the objects list + closes the modal
        return;
      }
      const r = await applyTableSchema(props.connectionId, props.database, props.table, buildRequest(), override);
      toast.success(`${r.applied} change${r.applied === 1 ? '' : 's'} applied`);
      setPreviewPlan(null);
      setViolations([]);
      setLoaded(false);
      await refetch();   // reload server snapshot so originalName fields realign
      initFromServer();
    } catch (e) {
      toast.error(`Apply failed: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  // Ctrl+S → preview (not apply directly — preview is the safety net).
  const onKey = (e) => {
    if (!props.active) return;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      startPreview();
    }
  };
  onMount(() => {
    document.addEventListener('keydown', onKey);
    onCleanup(() => document.removeEventListener('keydown', onKey));
    props.registerDirty?.(() => dirty());
  });

  return (
    <div class="script-editor table-editor">
      <div class="script-editor-toolbar">
        <Show when={dirty()}>
          <span class="script-editor-dirty" title="Unsaved changes">●</span>
        </Show>
        <Show when={createMode()}>
          <input class="input mono" style="width:180px;flex:0 0 auto;" placeholder="new_table_name"
                 value={tableName()} onInput={(e) => { setTableName(e.target.value); touch(); }} />
        </Show>

        <div class="tabbar">
          <button class={`tabbar-tab ${activeTab() === 'columns' ? 'active' : ''}`}
                  onClick={() => setActiveTab('columns')}>Columns</button>
          <button class={`tabbar-tab ${activeTab() === 'options' ? 'active' : ''}`}
                  onClick={() => setActiveTab('options')}>Options</button>
          <button class={`tabbar-tab ${activeTab() === 'indexes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('indexes')}>
            Indexes
            <Show when={indexes.length > 0}>
              <span class="muted" style="margin-left:4px;font-size:11px;">({indexes.length})</span>
            </Show>
          </button>
          <button class={`tabbar-tab ${activeTab() === 'fkeys' ? 'active' : ''}`}
                  onClick={() => setActiveTab('fkeys')}>
            Foreign keys
            <Show when={fkeys.length > 0}>
              <span class="muted" style="margin-left:4px;font-size:11px;">({fkeys.length})</span>
            </Show>
          </button>
          <button class={`tabbar-tab ${activeTab() === 'triggers' ? 'active' : ''}`}
                  onClick={() => setActiveTab('triggers')}>
            Triggers
            <Show when={triggers.length > 0}>
              <span class="muted" style="margin-left:4px;font-size:11px;">({triggers.length})</span>
            </Show>
          </button>
        </div>

        <div class="spacer" />
        <Show when={!createMode()}>
          <button class="btn btn-sm btn-ghost" onClick={() => { setLoaded(false); refetch(); }}
                  title="Discard local edits and re-load from the server"
                  disabled={previewing() || applying()}>
            ⟳ Reload
          </button>
        </Show>
        <button class="btn btn-sm" onClick={startPreview}
                disabled={(!dirty() && !createMode()) || previewing() || applying()}
                title={createMode() ? 'Preview the CREATE TABLE statements (Ctrl+S)' : 'Preview the ALTER TABLE statements (Ctrl+S)'}>
          {previewing() ? 'Planning…' : (createMode() ? '⟶ Create table' : '⟶ Preview & save')}
        </button>
      </div>

      <div class="script-editor-body">
        <Show when={!server.loading || loaded()} fallback={
          <div class="muted" style="padding:16px;">Loading schema…</div>
        }>
          <Show when={server.error} fallback={null}>
            <div class="muted" style="padding:16px;color:var(--danger,#ef4444);">
              {server.error.message}
            </div>
          </Show>

          <Show when={activeTab() === 'columns'}>
            <ColumnsTab
              columns={columns}
              types={typeList()}
              onUpdate={updateCol}
              onAdd={addCol}
              onRemove={removeCol}
              onMove={moveCol}
              onSetDefaultText={setDefaultText}
              onSetHasDefault={setHasDefault}
            />
          </Show>
          <Show when={activeTab() === 'options'}>
            <OptionsTab options={options} onSet={setOption} />
          </Show>
          <Show when={activeTab() === 'indexes'}>
            <IndexesTab
              indexes={indexes}
              onUpdate={updateIndex}
              onAdd={addIndex}
              onRemove={removeIndex}
            />
          </Show>
          <Show when={activeTab() === 'fkeys'}>
            <ForeignKeysTab
              fkeys={fkeys}
              onUpdate={updateFK}
              onAdd={addFK}
              onRemove={removeFK}
            />
          </Show>
          <Show when={activeTab() === 'triggers'}>
            <TriggersTab
              schema={sqlSchema}
              table={tableName()}
              triggers={triggers}
              onUpdate={updateTrigger}
              onAdd={addTrigger}
              onRemove={removeTrigger}
            />
          </Show>
        </Show>
      </div>

      <Show when={previewPlan()}>
        <PreviewModal
          steps={previewPlan()}
          violations={violations()}
          applying={applying()}
          onClose={closePreview}
          onApply={confirmApply}
        />
      </Show>
    </div>
  );
}

// ── Columns tab ──────────────────────────────────────────────────────────────
function ColumnsTab(props) {
  return (
    <div class="table-edit-columns">
      <div class="table-edit-columns-head">
        <span class="te-col-pos">#</span>
        <span class="te-col-name">Name</span>
        <span class="te-col-type">Type</span>
        <span class="te-col-flag">Null</span>
        <span class="te-col-flag">PK</span>
        <span class="te-col-flag">AI</span>
        <span class="te-col-default">Default</span>
        <span class="te-col-comment">Comment</span>
        <span class="te-col-actions"></span>
      </div>
      <For each={props.columns}>
        {(c, i) => (
          <div class="table-edit-columns-row">
            <span class="te-col-pos mono">{i() + 1}</span>
            <input class="input mono te-col-name" value={c.name}
                   onInput={(e) => props.onUpdate(i(), { name: e.target.value })}
                   placeholder="column_name" />
            <TypeEditor value={c.type} types={props.types}
                        title={c.collation ? `Collation: ${c.collation} (kept on save)` : undefined}
                        onChange={(t) => props.onUpdate(i(), { type: t })} />
            <label class="te-col-flag">
              <input type="checkbox" checked={c.nullable}
                     onChange={(e) => props.onUpdate(i(), { nullable: e.target.checked })} />
            </label>
            <label class="te-col-flag">
              <input type="checkbox" checked={c.isPrimary}
                     onChange={(e) => props.onUpdate(i(), { isPrimary: e.target.checked })} />
            </label>
            <label class="te-col-flag">
              <input type="checkbox" checked={c.autoIncrement}
                     onChange={(e) => props.onUpdate(i(), { autoIncrement: e.target.checked })} />
            </label>
            <span class="te-col-default">
              <label class="muted mono" style="font-size:10.5px;display:flex;align-items:center;gap:4px;">
                <input type="checkbox" checked={c.default !== null}
                       onChange={(e) => props.onSetHasDefault(i(), e.target.checked)} />
                Set
              </label>
              <input class="input mono" value={c.default ?? ''}
                     disabled={c.default === null}
                     onInput={(e) => props.onSetDefaultText(i(), e.target.value)}
                     placeholder={c.default === null ? '(no default)' : (c.defaultIsExpr ? 'expression, e.g. uuid()' : 'literal value (or NULL)')} />
              <Show when={c.default !== null && !c.generationExpr}>
                <label class="te-type-uns" title="Expression default — emitted as DEFAULT (expr) instead of a quoted literal">
                  <input type="checkbox" checked={!!c.defaultIsExpr}
                         onChange={(e) => props.onUpdate(i(), { defaultIsExpr: e.target.checked })} />
                  <span>expr</span>
                </label>
              </Show>
              <Show when={takesOnUpdate(c.type) && !c.generationExpr}>
                <label class="te-type-uns" title="ON UPDATE CURRENT_TIMESTAMP — refresh the value whenever the row is updated">
                  <input type="checkbox" checked={!!c.onUpdate}
                         onChange={(e) => props.onUpdate(i(), { onUpdate: e.target.checked ? onUpdateFor(c.type) : '' })} />
                  <span>on upd</span>
                </label>
              </Show>
            </span>
            <input class="input mono te-col-comment" value={c.comment}
                   onInput={(e) => props.onUpdate(i(), { comment: e.target.value })}
                   placeholder="comment" />
            <span class="te-col-actions">
              <button class="btn-link" title="Move up"   onClick={() => props.onMove(i(), -1)} disabled={i() === 0}>↑</button>
              <button class="btn-link" title="Move down" onClick={() => props.onMove(i(),  1)} disabled={i() === props.columns.length - 1}>↓</button>
              <button class="btn-link btn-link-danger" title="Remove column"
                      onClick={() => props.onRemove(i())}>×</button>
            </span>
          </div>
        )}
      </For>
      <div class="table-edit-columns-foot">
        <button class="btn btn-sm" onClick={props.onAdd}>+ Add column</button>
      </div>
    </div>
  );
}

// ── Type editor (caps-driven dropdown) ───────────────────────────────────────
// The column data model stays a plain type STRING ("varchar(255)", "int unsigned",
// "decimal(10,2)", "datetime(6)"). TypeEditor is purely an input affordance: it
// parses that string into {base, args, unsigned}, presents a dropdown limited to the
// server-supported types (props.types, caps-driven from the backend), and rebuilds
// the canonical string on change. Strict — there is no free-text path — but an
// EXISTING column whose base type isn't in the catalog is preserved as a "(current)"
// option so editing never silently rewrites a type Ninocat doesn't list.

// parseType splits a raw type string into editable parts. The parens group is
// greedy (\(.*\)) so enum/set value lists containing ')' survive intact.
function parseType(raw) {
  const s = (raw || '').trim();
  const m = s.match(/^([A-Za-z][A-Za-z0-9]*)\s*(?:\((.*)\))?\s*(.*)$/);
  if (!m) return { base: '', args: '', unsigned: false, zerofill: false };
  const rest = (m[3] || '').toLowerCase();
  return {
    base: m[1].toLowerCase(),
    args: (m[2] || '').trim(),
    unsigned: /\bunsigned\b/.test(rest),
    zerofill: /\bzerofill\b/.test(rest),
  };
}

// ON UPDATE helpers. EXTRA reads "on update CURRENT_TIMESTAMP(3)" (MySQL, maybe
// after DEFAULT_GENERATED) or "on update current_timestamp()" (MariaDB).
function onUpdateFromExtra(extra) {
  const m = /\bon update\s+(.+)$/i.exec(extra || '');
  return m ? m[1].trim() : '';
}
// ON UPDATE CURRENT_TIMESTAMP is only valid on TIMESTAMP / DATETIME.
function takesOnUpdate(type) {
  const b = parseType(type).base;
  return b === 'timestamp' || b === 'datetime';
}
// The clause must carry the column's fractional-seconds precision
// (DATETIME(3) needs CURRENT_TIMESTAMP(3) — a mismatch is rejected).
function onUpdateFor(type) {
  const fsp = parseInt(parseType(type).args, 10);
  return fsp > 0 ? `CURRENT_TIMESTAMP(${fsp})` : 'CURRENT_TIMESTAMP';
}

function buildType(base, args, unsigned, zerofill) {
  let t = base || '';
  const a = (args ?? '').trim();
  if (a !== '') t += `(${a})`;
  if (unsigned) t += ' unsigned';
  if (zerofill) t += ' zerofill';
  return t;
}

function argsPlaceholder(dt) {
  switch (dt?.param) {
    case 'length':
    case 'lengthR': return 'length';
    case 'decimal': return 'precision,scale';
    case 'enum':    return "'a','b'";
    case 'fsp':     return 'fsp 0-6';
    default:        return '';
  }
}

function TypeEditor(props) {
  // custom() = raw free-text escape hatch. Lets the user type ANY type verbatim —
  // a newer server's type the catalog doesn't list yet, or a manual override if the
  // structured editor would ever mishandle an unusual type — bypassing parse/rebuild
  // entirely (props.value is written through untouched).
  const [custom, setCustom] = createSignal(false);
  const parsed = () => parseType(props.value);
  const byName = (n) => (props.types || []).find((t) => t.name === n);
  const current = () => byName(parsed().base);
  const baseKnown = () => !!current();

  // Distinct categories in catalog order, for <optgroup> grouping.
  const categories = () => {
    const seen = [];
    for (const t of props.types || []) if (!seen.includes(t.category)) seen.push(t.category);
    return seen;
  };
  const typesIn = (cat) => (props.types || []).filter((t) => t.category === cat);

  // Show the argument box only for the currently selected type's shape; the
  // fractional-seconds arg appears only where the server supports it (dt.fsp).
  const showArgs = () => {
    const dt = current();
    if (!dt) return parsed().args !== '';   // unknown/current type: only if it already has args
    if (dt.param === 'fsp') return !!dt.fsp;
    return dt.param !== '';
  };

  const emit = (base, args, unsigned, zerofill) =>
    props.onChange(buildType(base, args, unsigned, zerofill));

  // Picking a new base type carries args sensibly: clear them for a no-arg type,
  // prefill the catalog default when the new type needs args and there are none,
  // and drop UNSIGNED if the new type can't take it.
  const onPickBase = (name) => {
    const dt = byName(name);
    const p = parsed();
    let args = p.args;
    if (dt) {
      if (dt.param === '') args = '';
      else if (dt.param === 'fsp') { if (!dt.fsp) args = ''; }
      else if (!args && dt.default) args = dt.default;
    }
    const unsigned = dt && dt.unsigned ? p.unsigned : false;
    emit(name, args, unsigned, false);
  };

  return (
    <Show
      when={!custom()}
      fallback={
        <span class="te-type-edit" title={props.title}>
          <input class="input mono te-type-raw" value={props.value}
                 placeholder="e.g. varchar(255)"
                 onInput={(e) => props.onChange(e.target.value)} />
          <button type="button" class="btn-link te-type-back" title="Back to the type list"
                  onClick={() => setCustom(false)}>list</button>
        </span>
      }
    >
      <span class="te-type-edit" title={props.title}>
        <select class="input mono te-type-base" value={parsed().base}
                onChange={(e) => {
                  if (e.target.value === '__custom__') { setCustom(true); return; }
                  onPickBase(e.target.value);
                }}>
          <Show when={!baseKnown() && parsed().base !== ''}>
            <option value={parsed().base}>{props.value} (current)</option>
          </Show>
          <For each={categories()}>
            {(cat) => (
              <optgroup label={cat}>
                <For each={typesIn(cat)}>
                  {(t) => <option value={t.name}>{t.label}</option>}
                </For>
              </optgroup>
            )}
          </For>
          <option value="__custom__">Custom…</option>
        </select>
        <Show when={showArgs()}>
          <input class="input mono te-type-args" value={parsed().args}
                 placeholder={argsPlaceholder(current())}
                 onInput={(e) => { const p = parsed(); emit(p.base, e.target.value, p.unsigned, p.zerofill); }} />
        </Show>
        <Show when={current()?.unsigned}>
          <label class="te-type-uns" title="UNSIGNED">
            <input type="checkbox" checked={parsed().unsigned}
                   onChange={(e) => { const p = parsed(); emit(p.base, p.args, e.target.checked, p.zerofill); }} />
            <span>uns</span>
          </label>
        </Show>
      </span>
    </Show>
  );
}

// ── Options tab ──────────────────────────────────────────────────────────────
function OptionsTab(props) {
  const F = (label, child) => (
    <div class="table-edit-option-row">
      <label class="muted">{label}</label>
      <div>{child}</div>
    </div>
  );
  return (
    <div class="table-edit-options">
      {F('Engine',
        <select class="input" value={props.options.engine}
                onChange={(e) => props.onSet('engine', e.target.value)}>
          <For each={ENGINES}>
            {(en) => <option value={en} selected={props.options.engine === en}>{en}</option>}
          </For>
        </select>
      )}
      {F('Default character set',
        <input class="input mono" value={props.options.charset}
               placeholder="utf8mb4"
               onInput={(e) => props.onSet('charset', e.target.value)} />
      )}
      {F('Default collation',
        <input class="input mono" value={props.options.collation}
               placeholder="utf8mb4_general_ci"
               onInput={(e) => props.onSet('collation', e.target.value)} />
      )}
      {F('AUTO_INCREMENT (next value)',
        <input class="input mono" type="number" min="0" value={props.options.autoIncrement}
               onInput={(e) => props.onSet('autoIncrement', Number(e.target.value) || 0)} />
      )}
      {F('Row format',
        <select class="input" value={props.options.rowFormat}
                onChange={(e) => props.onSet('rowFormat', e.target.value)}>
          <For each={ROWFORMATS}>
            {(rf) => <option value={rf} selected={props.options.rowFormat === rf}>{rf || '(none)'}</option>}
          </For>
        </select>
      )}
      {F('Comment',
        <textarea class="input" rows="2" value={props.options.comment}
                  onInput={(e) => props.onSet('comment', e.target.value)} />
      )}
    </div>
  );
}

// ── Indexes tab ──────────────────────────────────────────────────────────────
const INDEX_TYPES = ['BTREE', 'HASH', 'FULLTEXT', 'SPATIAL'];

// Split on commas that are outside parentheses / quotes, so a functional key
// part like (concat(a, b)) stays one item.
function splitKeyParts(text) {
  const out = [];
  let depth = 0, cur = '', quote = null;
  for (const ch of text || '') {
    if (quote) {
      cur += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') { quote = ch; cur += ch; continue; }
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    if (ch === ',' && depth === 0) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim()).filter(Boolean);
}

// "tenant_id, name(50) DESC, (lower(email))" → [{column,length,desc}|{expression,desc}]
// A parenthesized part is a functional key part (MySQL 8.0.13+), kept opaque.
function parseIndexColumns(text) {
  return splitKeyParts(text).map((raw) => {
    let s = raw;
    let desc = false;
    const dm = s.match(/^(.*\S)\s+(ASC|DESC)$/i);
    if (dm) { s = dm[1]; desc = dm[2].toUpperCase() === 'DESC'; }
    const extra = desc ? { desc: true } : {};
    if (s.startsWith('(') && s.endsWith(')')) {
      return { column: '', expression: s.slice(1, -1).trim(), ...extra };
    }
    const m = s.match(/^([^(]+?)(?:\((\d+)\))?$/);
    if (!m) return { column: s, ...extra };
    const len = m[2] ? Number(m[2]) : 0;
    return len > 0 ? { column: m[1].trim(), length: len, ...extra } : { column: m[1].trim(), ...extra };
  });
}
function indexColsToText(cols) {
  return cols.map((c) => {
    let t = c.expression ? `(${c.expression})` : (c.length ? `${c.column}(${c.length})` : c.column);
    if (c.desc) t += ' DESC';
    return t;
  }).join(', ');
}
// Plain "a, b, c" → ["a","b","c"]. Used for FK column lists.
function parseColumnList(text) {
  return (text || '').split(',').map((s) => s.trim()).filter(Boolean);
}

function IndexesTab(props) {
  return (
    <div class="te-compact-list">
      <Show when={props.indexes.length === 0}>
        <div class="muted te-compact-empty">No indexes on this table.</div>
      </Show>
      <Show when={props.indexes.length > 0}>
        <div class="te-compact-head te-index-grid">
          <span>Name</span>
          <span>Type</span>
          <span title="UNIQUE constraint — N/A for FULLTEXT/SPATIAL">U</span>
          <span>Columns <span class="muted" style="text-transform:none;font-weight:400;">(comma-sep; <code>name(50)</code> prefix, <code>DESC</code>, <code>(expr)</code>)</span></span>
          <span>Comment</span>
          <span />
        </div>
      </Show>
      <For each={props.indexes}>
        {(ix, i) => (
          <div class="te-compact-row te-index-grid">
            <input class="input mono" value={ix.name} placeholder="index_name"
                   onInput={(e) => props.onUpdate(i(), { name: e.target.value })} />
            <select class="input" value={ix.type}
                    onChange={(e) => props.onUpdate(i(), { type: e.target.value })}>
              <For each={INDEX_TYPES}>{(v) => <option value={v} selected={ix.type === v}>{v}</option>}</For>
            </select>
            <input type="checkbox" checked={ix.isUnique}
                   disabled={ix.type === 'FULLTEXT' || ix.type === 'SPATIAL'}
                   onChange={(e) => props.onUpdate(i(), { isUnique: e.target.checked })} />
            <input class="input mono" value={ix.columnsText} placeholder="tenant_id, name(50)"
                   onInput={(e) => props.onUpdate(i(), { columnsText: e.target.value })} />
            <input class="input mono" value={ix.comment} placeholder="comment"
                   onInput={(e) => props.onUpdate(i(), { comment: e.target.value })} />
            <div class="te-compact-actions">
              <Show when={ix.originalName}>
                <span class="muted mono te-existing-tag" title="Exists on the server">●</span>
              </Show>
              <button class="btn-link btn-link-danger" onClick={() => props.onRemove(i())}>×</button>
            </div>
          </div>
        )}
      </For>
      <div class="te-compact-foot">
        <button class="btn btn-sm" onClick={props.onAdd}>+ Add index</button>
      </div>
    </div>
  );
}

// ── Foreign keys tab ─────────────────────────────────────────────────────────
const FK_ACTIONS = ['RESTRICT', 'CASCADE', 'SET NULL', 'NO ACTION', 'SET DEFAULT'];

function ForeignKeysTab(props) {
  return (
    <div class="te-compact-list">
      <Show when={props.fkeys.length === 0}>
        <div class="muted te-compact-empty">No foreign keys on this table.</div>
      </Show>
      <Show when={props.fkeys.length > 0}>
        <div class="te-compact-head te-fk-grid">
          <span>Name</span>
          <span>Local columns</span>
          <span>Ref. schema</span>
          <span>Ref. table</span>
          <span>Ref. columns</span>
          <span>ON UPDATE</span>
          <span>ON DELETE</span>
          <span />
        </div>
      </Show>
      <For each={props.fkeys}>
        {(fk, i) => (
          <div class="te-compact-row te-fk-grid">
            <input class="input mono" value={fk.name} placeholder="fk_name"
                   onInput={(e) => props.onUpdate(i(), { name: e.target.value })} />
            <input class="input mono" value={fk.columnsText} placeholder="user_id"
                   onInput={(e) => props.onUpdate(i(), { columnsText: e.target.value })} />
            <input class="input mono" value={fk.referencedSchema} placeholder="(same)"
                   onInput={(e) => props.onUpdate(i(), { referencedSchema: e.target.value })} />
            <input class="input mono" value={fk.referencedTable} placeholder="users"
                   onInput={(e) => props.onUpdate(i(), { referencedTable: e.target.value })} />
            <input class="input mono" value={fk.referencedColsText} placeholder="id"
                   onInput={(e) => props.onUpdate(i(), { referencedColsText: e.target.value })} />
            <select class="input" value={fk.onUpdate}
                    onChange={(e) => props.onUpdate(i(), { onUpdate: e.target.value })}>
              <For each={FK_ACTIONS}>{(v) => <option value={v} selected={fk.onUpdate === v}>{v}</option>}</For>
            </select>
            <select class="input" value={fk.onDelete}
                    onChange={(e) => props.onUpdate(i(), { onDelete: e.target.value })}>
              <For each={FK_ACTIONS}>{(v) => <option value={v} selected={fk.onDelete === v}>{v}</option>}</For>
            </select>
            <div class="te-compact-actions">
              <Show when={fk.originalName}>
                <span class="muted mono te-existing-tag" title="Exists on the server">●</span>
              </Show>
              <button class="btn-link btn-link-danger" onClick={() => props.onRemove(i())}>×</button>
            </div>
          </div>
        )}
      </For>
      <div class="te-compact-foot">
        <button class="btn btn-sm" onClick={props.onAdd}>+ Add foreign key</button>
      </div>
    </div>
  );
}

// ── Triggers tab ─────────────────────────────────────────────────────────────
const TIMINGS = ['BEFORE', 'AFTER'];
const EVENTS  = ['INSERT', 'UPDATE', 'DELETE'];

function TriggersTab(props) {
  return (
    <div class="te-compact-list">
      <Show when={props.triggers.length === 0}>
        <div class="muted te-compact-empty">No triggers on this table.</div>
      </Show>
      <For each={props.triggers}>
        {(t, i) => (
          <div class="te-trigger-card">
            <div class="te-compact-row te-trigger-head-grid">
              <input class="input mono" value={t.name} placeholder="trigger_name"
                     onInput={(e) => props.onUpdate(i(), { name: e.target.value })} />
              <select class="input" value={t.timing}
                      onChange={(e) => props.onUpdate(i(), { timing: e.target.value })}>
                <For each={TIMINGS}>{(v) => <option value={v} selected={t.timing === v}>{v}</option>}</For>
              </select>
              <select class="input" value={t.event}
                      onChange={(e) => props.onUpdate(i(), { event: e.target.value })}>
                <For each={EVENTS}>{(v) => <option value={v} selected={t.event === v}>{v}</option>}</For>
              </select>
              <div class="te-compact-actions">
                <Show when={t.originalName}>
                  <span class="muted mono te-existing-tag" title="Exists on the server">●</span>
                </Show>
                <button class="btn-link btn-link-danger"
                        onClick={() => props.onRemove(i())}>×</button>
              </div>
            </div>
            <div class="te-trigger-body">
              <CodeMirrorSQL
                value={t.statement}
                onInput={(v) => props.onUpdate(i(), { statement: v })}
                height="140px"
                schema={props.schema}
                triggerTable={props.table}
              />
            </div>
          </div>
        )}
      </For>
      <div class="te-compact-foot">
        <button class="btn btn-sm" onClick={props.onAdd}>+ Add trigger</button>
      </div>
    </div>
  );
}

// ── Preview modal ────────────────────────────────────────────────────────────
function PreviewModal(props) {
  const hasViolations = () => (props.violations || []).length > 0;
  // Required before Apply is enabled whenever the target can't support something
  // this change needs — see dbcaps.Check / the "block, with override" policy.
  const [ack, setAck] = createSignal(false);
  const blocked = () => hasViolations() && !ack();

  return (
    <div class="modal-backdrop" onClick={props.onClose}>
      <div class="modal-card table-edit-preview" onClick={(e) => e.stopPropagation()}>
        <h3>Preview changes</h3>
        <Show when={hasViolations()}>
          <div class="table-edit-preview-violations">
            <div class="table-edit-preview-violations-head">
              ⛔ The target server's version can't support {props.violations.length === 1 ? 'this' : 'these'}:
            </div>
            <ul>
              <For each={props.violations}>{(v) => <li>{v.reason}</li>}</For>
            </ul>
            <label class="table-edit-preview-ack">
              <input type="checkbox" checked={ack()} onChange={(e) => setAck(e.target.checked)} />
              I understand this may be rejected by the server — apply anyway
            </label>
          </div>
        </Show>
        <Show when={props.steps.length === 0} fallback={
          <>
            <p class="muted" style="margin:0 0 10px;font-size:12px;">
              {props.steps.length} statement{props.steps.length === 1 ? '' : 's'} will be executed in order.
              MySQL DDL is auto-committed and cannot be rolled back.
            </p>
            <Show when={props.steps.some((s) => s.warning)}>
              <div class="table-edit-preview-warnbanner">
                ⚠ Some changes can <strong>lose or truncate existing data</strong> (highlighted below).
                On a non-strict MySQL server this happens silently — review carefully, and consider backing up first.
              </div>
            </Show>
            <div class="table-edit-preview-steps">
              <For each={props.steps}>
                {(s, i) => (
                  <div class={`table-edit-preview-step ${s.warning ? 'has-warn' : ''}`}>
                    <div class="table-edit-preview-summary">
                      <span class="mono muted">{i() + 1}.</span>
                      {s.summary}
                    </div>
                    <pre class="table-edit-preview-sql mono">{s.sql}</pre>
                    <Show when={s.warning}>
                      <div class="table-edit-preview-warn">⚠ {s.warning}</div>
                    </Show>
                  </div>
                )}
              </For>
            </div>
            <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={props.onClose} disabled={props.applying}>Cancel</button>
              <button class="btn" onClick={() => props.onApply(ack())} disabled={props.applying || blocked()}
                      title={blocked() ? 'Check the acknowledgement above to proceed' : undefined}>
                {props.applying ? 'Applying…' : (hasViolations() ? 'Apply anyway' : 'Apply')}
              </button>
            </div>
          </>
        }>
          <p class="muted">No changes to apply.</p>
          <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end;">
            <button class="btn" onClick={props.onClose}>Close</button>
          </div>
        </Show>
      </div>
    </div>
  );
}
